/**
 * ProjectActivity - Display SSE events grouped by project.
 *
 * Pure rendering component - just displays events from the store.
 * SSE connection managed globally in App.tsx.
 */

import type { JSX } from 'react';
import { BoltIcon } from '@heroicons/react/24/outline';

import { useSSEStore } from '~/stores/sseStore';

import { ConnectionStatus } from './ConnectionStatus';
import { ProjectActivityItem } from './Item';

export function ProjectActivity(): JSX.Element {
  const { activeJobs, clearProjectEvents, connectionStatus, eventsByProject } = useSSEStore();

  // Get projects with active jobs and events (deduplicated)
  const activeProjectIds = Array.from(new Set(Object.values(activeJobs).map(job => job.projectId)));
  const projectsWithEvents = activeProjectIds.filter(id => (eventsByProject[id]?.length || 0) > 0);

  const hasEvents = projectsWithEvents.length > 0;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="p-4 flex-1 flex flex-col overflow-hidden">
        {/* Connection Status */}
        <ConnectionStatus status={connectionStatus} />

        {/* Empty State */}
        {!hasEvents ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted max-w-sm">
              <BoltIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Progress Updates</h3>
              <p className="text-sm">
                Create a project with a PRD to see real-time progress updates here.
              </p>
            </div>
          </div>
        ) : (
          /* Project Groups */
          <div className="flex-1 overflow-y-auto space-y-4">
            {projectsWithEvents.map(projectId => {
              const events = eventsByProject[projectId] || [];
              const isActiveJob = activeProjectIds.includes(projectId);
              return (
                <ProjectActivityItem
                  key={projectId}
                  projectId={projectId}
                  events={events}
                  isActiveJob={isActiveJob}
                  onClear={() => clearProjectEvents(projectId)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
